<template lang="pug">
div
	ul.nav.nav-tabs(role="tablist")
		li.nav-item(
			v-for="tab in tabs",
			:key="tab.uid",
			role="presentation"
		)
			a.nav-link(
				href="javascript:void(0);",
				role="tab",
				:class="{ active: tab.uid === activeUid, disabled: tab.disabled }",
				:aria-selected="tab.uid === activeUid",
				@click="activate(tab.uid)"
			)
				span(:id="titleMountId(tab.uid)")
	div.tab-content(:class="contentClass")
		slot
</template>

<script>
export default {
	name: "BTabs",
	props: {
		contentClass: null,
		modelValue: {
			type: Number,
			default: 0,
		},
	},
	emits: ["update:modelValue", "input", "activate-tab"],
	data() {
		return {
			tabs: [],
			activeUid: null,
		};
	},
	created() {
		this._tabVms = new Map();
	},
	watch: {
		modelValue(index) {
			const tab = this.tabs[index];
			if (tab && tab.uid !== this.activeUid) {
				this.activeUid = tab.uid;
			}
		},
	},
	provide() {
		return {
			btabs: this,
		};
	},
	mounted() {
		this.$nextTick(() => {
			if (!this.tabs.length) {
				return;
			}
			const initial = this.tabs[this.modelValue] ?? this.tabs[0];
			if (initial) {
				this.activeUid = initial.uid;
			}
		});
	},
	methods: {
		titleMountId(uid) {
			return `btab-title-${uid}`;
		},
		register(tabVm) {
			this._tabVms.set(tabVm.uid, tabVm);

			const entry = {
				uid: tabVm.uid,
				disabled: !!tabVm.disabled,
			};

			const existing = this.tabs.findIndex((t) => t.uid === tabVm.uid);
			if (existing === -1) {
				this.tabs.push(entry);
			} else {
				this.tabs.splice(existing, 1, entry);
			}

			// Always keep a valid active tab. Tabs often mount after BTabs.mounted()
			// nextTick (form v-if / async advertiser), so mounted alone is not enough.
			if (this.activeUid == null || !this.tabs.some((t) => t.uid === this.activeUid)) {
				const preferred = this.tabs.find((t) => t.uid === tabVm.uid && tabVm.active)
					?? this.tabs.find((t) => !t.disabled)
					?? this.tabs[0];
				this.activeUid = preferred?.uid ?? null;
			}

			// Resolve the nav mount after this registration has patched the DOM.
			// Title element state lives on BTab only — never write it during BTabs render.
			this.$nextTick(() => {
				tabVm.setTitleMount?.(document.getElementById(this.titleMountId(tabVm.uid)));
			});
		},
		unregister(uid) {
			const tabVm = this._tabVms.get(uid);
			tabVm?.setTitleMount?.(null);
			this._tabVms.delete(uid);

			const index = this.tabs.findIndex((t) => t.uid === uid);
			if (index === -1) {
				return;
			}
			this.tabs.splice(index, 1);
			if (this.activeUid === uid || !this.tabs.some((t) => t.uid === this.activeUid)) {
				this.activeUid = this.tabs.find((t) => !t.disabled)?.uid ?? this.tabs[0]?.uid ?? null;
				this.syncModel();
			}
		},
		setDisabled(uid, disabled) {
			const tab = this.tabs.find((t) => t.uid === uid);
			if (!tab || tab.disabled === disabled) {
				return;
			}
			tab.disabled = disabled;
			if (disabled && this.activeUid === uid) {
				this.activeUid = this.tabs.find((t) => !t.disabled)?.uid ?? null;
				this.syncModel();
			}
		},
		activate(uid) {
			const tab = this.tabs.find((t) => t.uid === uid);
			if (!tab || tab.disabled) {
				return;
			}

			const prevIndex = this.activeIndex;
			const nextIndex = this.getTabIndex(uid);
			if (this.activeUid === uid) {
				return;
			}

			this.activeUid = uid;
			this.syncModel();
			this.$emit("activate-tab", nextIndex, prevIndex);
		},
		getTabIndex(uid) {
			return this.tabs.findIndex((t) => t.uid === uid);
		},
		syncModel() {
			const index = this.activeIndex;
			if (index === this.modelValue) {
				return;
			}
			this.$emit("update:modelValue", index);
			this.$emit("input", index);
		},
	},
	computed: {
		activeIndex() {
			return this.getTabIndex(this.activeUid);
		},
	},
};
</script>
